// src/pages/GemCatalog/GemCatalog.jsx

import React, { useEffect, useState } from "react";
import { Modal, Form, Input, Upload, message, Button, InputNumber, Spin } from "antd";
import {
    PlusOutlined,
    UploadOutlined,
    EditOutlined,
    DeleteOutlined,
    PictureOutlined,
    ExclamationCircleOutlined,
} from "@ant-design/icons";
import { Gem } from "lucide-react";
import inventory from "../../api/inventoryApi";
import "../../components/AddUserModal/style.css";

const IMAGE_BASE_URL = "https://api.duotetra.quest/uploads/gems/";

function cn(...c) {
    return c.filter(Boolean).join(" ");
}

const shellClass =
    "relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-xl shadow-[0_18px_60px_-30px_rgba(0,0,0,0.9)]";

const inputNumGlass =
    "w-full !rounded-xl [&_.ant-input-number-input]:!text-white/90 [&_.ant-input-number-handler-wrap]:!border-white/10";

const inputGlass =
    "!rounded-xl !border-white/10 !bg-white/[0.06] !text-white/90 hover:!border-white/[0.18] focus:!border-emerald-400/50";

const uploadBtnClass =
    "!rounded-xl !border-white/10 !bg-white/[0.06] !text-white/85 hover:!border-white/20 hover:!bg-white/10";

export default function GemCatalogPage() {
    const [gems, setGems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [form] = Form.useForm();

    const [currentImages, setCurrentImages] = useState({
        front: "",
        back: "",
        secondary: "",
    });

    const [editingGem, setEditingGem] = useState(null);

    const [fileListFront, setFileListFront] = useState([]);
    const [fileListBack, setFileListBack] = useState([]);
    const [fileListSecondary, setFileListSecondary] = useState([]);

    const fetchGems = async () => {
        setLoading(true);
        try {
            const list = await inventory.gemCatalogApi.getAllGems();
            setGems(list);
        } catch (e) {
            console.error("Ошибка при загрузке гемов:", e);
            message.error("Не удалось загрузить каталог");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchGems();
    }, []);

    const normFile = (e) => {
        if (Array.isArray(e)) {
            return e;
        }
        return e?.fileList;
    };

    const openModal = (gem = null) => {
        setEditingGem(gem);
        form.resetFields();

        if (gem) {
            const frontVirtual = gem.frontImage
                ? [
                      {
                          uid: "-1",
                          name: gem.frontImage,
                          status: "done",
                          url: IMAGE_BASE_URL + gem.frontImage,
                          originFileObj: null,
                      },
                  ]
                : [];

            const backVirtual = gem.backImage
                ? [
                      {
                          uid: "-2",
                          name: gem.backImage,
                          status: "done",
                          url: IMAGE_BASE_URL + gem.backImage,
                          originFileObj: null,
                      },
                  ]
                : [];

            const secondaryVirtual = gem.backImageSecondary
                ? [
                      {
                          uid: "-3",
                          name: gem.backImageSecondary,
                          status: "done",
                          url: IMAGE_BASE_URL + gem.backImageSecondary,
                          originFileObj: null,
                      },
                  ]
                : [];

            setFileListFront(frontVirtual);
            setFileListBack(backVirtual);
            setFileListSecondary(secondaryVirtual);

            form.setFieldsValue({
                name: gem.name,
                position: gem.position,
                frontImage: frontVirtual,
                backImage: backVirtual,
                backImageSecondary: secondaryVirtual,
            });

            setCurrentImages({
                front: gem.frontImage || "",
                back: gem.backImage || "",
                secondary: gem.backImageSecondary || "",
            });
        } else {
            setFileListFront([]);
            setFileListBack([]);
            setFileListSecondary([]);
            setCurrentImages({ front: "", back: "", secondary: "" });
            form.setFieldsValue({
                frontImage: [],
                backImage: [],
                backImageSecondary: [],
            });
        }
        setModalOpen(true);
    };

    const handleFrontChange = ({ fileList }) => {
        const last = fileList.slice(-1);
        setFileListFront(last);
        form.setFieldsValue({ frontImage: last });
    };
    const handleBackChange = ({ fileList }) => {
        const last = fileList.slice(-1);
        setFileListBack(last);
        form.setFieldsValue({ backImage: last });
    };
    const handleSecondaryChange = ({ fileList }) => {
        const last = fileList.slice(-1);
        setFileListSecondary(last);
        form.setFieldsValue({ backImageSecondary: last });
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();

            if (!editingGem) {
                if (!values.frontImage || values.frontImage.length === 0) {
                    message.error("При создании обязательно загрузите Front изображение");
                    return;
                }
                if (!values.backImage || values.backImage.length === 0) {
                    message.error("При создании обязательно загрузите Back изображение");
                    return;
                }
            }

            const formData = new FormData();
            formData.append("name", values.name);
            formData.append("position", values.position);

            if (
                values.frontImage &&
                values.frontImage.length > 0 &&
                values.frontImage[0].originFileObj
            ) {
                formData.append("frontImage", values.frontImage[0].originFileObj);
            } else if (editingGem && currentImages.front) {
                formData.append("frontImage", currentImages.front);
            }

            if (
                values.backImage &&
                values.backImage.length > 0 &&
                values.backImage[0].originFileObj
            ) {
                formData.append("backImage", values.backImage[0].originFileObj);
            } else if (editingGem && currentImages.back) {
                formData.append("backImage", currentImages.back);
            }

            if (
                values.backImageSecondary &&
                values.backImageSecondary.length > 0 &&
                values.backImageSecondary[0].originFileObj
            ) {
                formData.append("backImageSecondary", values.backImageSecondary[0].originFileObj);
            } else if (editingGem && currentImages.secondary) {
                formData.append("backImageSecondary", currentImages.secondary);
            }

            if (editingGem) {
                await inventory.gemCatalogApi.updateGem(editingGem.id, formData);
                message.success("Кристалл обновлён");
            } else {
                await inventory.gemCatalogApi.createGem(formData);
                message.success("Кристалл добавлен");
            }

            setModalOpen(false);
            fetchGems();
        } catch (error) {
            const errorMessage = error.response?.data?.message || "Ошибка при сохранении данных";
            message.error(errorMessage);
            console.error(error);
        }
    };

    const handleDelete = (id) => {
        Modal.confirm({
            title: "Удалить кристалл из каталога?",
            icon: <ExclamationCircleOutlined className="text-rose-400" />,
            content: "Действие необратимо.",
            okText: "Удалить",
            okType: "danger",
            cancelText: "Отмена",
            className: "custom-modal",
            centered: true,
            onOk: async () => {
                try {
                    await inventory.gemCatalogApi.deleteGem(id);
                    message.success("Удалено");
                    fetchGems();
                } catch (e) {
                    const errorMessage = e.response?.data?.message || "Ошибка при удалении";
                    message.error(errorMessage);
                    console.error(e);
                }
            },
        });
    };

    const sortedGems = [...gems].sort((a, b) => a.position - b.position);

    const thumbWrap =
        "flex h-16 w-16 items-center justify-center rounded-lg border border-white/10 bg-black/25 p-1 shadow-inner";

    if (loading && !gems.length) {
        return (
            <div className="flex min-h-[calc(100dvh-1.5rem)] flex-1 flex-col gap-4 p-4 text-white sm:p-5">
                <section className={cn(shellClass, "flex min-h-[280px] flex-1 items-center justify-center p-12")}>
                    <Spin size="large" className="[&_.ant-spin-dot-item]:!bg-emerald-400" />
                </section>
            </div>
        );
    }

    return (
        <div className="flex min-h-[calc(100dvh-1.5rem)] flex-1 flex-col gap-4 p-4 text-white sm:p-5">
            <section className={cn(shellClass, "shrink-0 p-4 sm:p-5")}>
                <div className="pointer-events-none absolute -right-10 -top-10 h-36 w-36 rounded-full bg-violet-500/10 blur-3xl" />
                <div className="relative flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex items-center gap-3">
                        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white/[0.06]">
                            <Gem className="h-5 w-5 text-violet-300/90" strokeWidth={2} />
                        </span>
                        <div>
                            <h1 className="text-lg font-semibold tracking-tight text-white/95 sm:text-xl">Кристаллы</h1>
                            <p className="text-[12px] text-white/45">Каталог изображений front / back / доп. слой</p>
                        </div>
                    </div>
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => openModal()}
                        className="!rounded-xl !border-0 !bg-emerald-500 !text-[#070A12] hover:!bg-emerald-400"
                        size="large"
                    >
                        Добавить кристалл
                    </Button>
                </div>
            </section>

            <section className={cn(shellClass, "relative flex min-h-0 flex-1 flex-col overflow-hidden")}>
                {loading && gems.length > 0 && (
                    <div className="absolute inset-0 z-10 flex items-center justify-center rounded-2xl bg-[#070A12]/45 backdrop-blur-sm">
                        <Spin size="large" className="[&_.ant-spin-dot-item]:!bg-emerald-400" />
                    </div>
                )}
                <div className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-5">
                    {sortedGems.length > 0 ? (
                        <div className="flex flex-wrap gap-4">
                            {sortedGems.map((gem) => (
                                <div
                                    key={gem.id}
                                    className={cn(
                                        shellClass,
                                        "flex w-56 shrink-0 flex-col items-center gap-3 p-4 transition-colors hover:border-white/[0.14]"
                                    )}
                                >
                                    <div className="text-center text-[14px] font-semibold text-white/90">
                                        <span className="text-violet-300/90">{gem.position}.</span> {gem.name}
                                    </div>

                                    <div className="h-px w-full bg-white/10" />

                                    <div className="flex flex-row gap-3">
                                        <div className="flex flex-col items-center">
                                            <div className={thumbWrap}>
                                                <img
                                                    src={`${IMAGE_BASE_URL}${gem.frontImage}`}
                                                    alt={`${gem.name} front`}
                                                    className="h-full w-full rounded object-contain"
                                                />
                                            </div>
                                            <span className="mt-1 flex items-center gap-1 text-[11px] text-white/40">
                                                <PictureOutlined /> Front
                                            </span>
                                        </div>

                                        <div className="flex flex-col items-center">
                                            <div className={thumbWrap}>
                                                <img
                                                    src={`${IMAGE_BASE_URL}${gem.backImage}`}
                                                    alt={`${gem.name} back`}
                                                    className="h-full w-full rounded object-contain"
                                                />
                                            </div>
                                            <span className="mt-1 flex items-center gap-1 text-[11px] text-white/40">
                                                <PictureOutlined /> Back
                                            </span>
                                        </div>

                                        <div className="flex flex-col items-center">
                                            <div className={thumbWrap}>
                                                {gem.backImageSecondary ? (
                                                    <img
                                                        src={`${IMAGE_BASE_URL}${gem.backImageSecondary}`}
                                                        alt={`${gem.name} back secondary`}
                                                        className="h-full w-full rounded object-contain"
                                                    />
                                                ) : (
                                                    <div className="flex h-full w-full items-center justify-center text-[10px] text-white/30">
                                                        —
                                                    </div>
                                                )}
                                            </div>
                                            <span className="mt-1 flex items-center gap-1 text-[11px] text-white/40">
                                                <PictureOutlined /> Back 2
                                            </span>
                                        </div>
                                    </div>

                                    <div className="h-px w-full bg-white/10" />

                                    <div className="flex gap-2">
                                        <Button
                                            type="text"
                                            icon={<EditOutlined className="text-sky-300" />}
                                            onClick={() => openModal(gem)}
                                            className="!rounded-lg !border !border-white/10 !bg-white/[0.06] hover:!border-white/20 hover:!bg-white/10"
                                        />
                                        <Button
                                            type="text"
                                            icon={<DeleteOutlined className="text-rose-400" />}
                                            onClick={() => handleDelete(gem.id)}
                                            className="!rounded-lg !border !border-white/10 !bg-white/[0.06] hover:!border-rose-400/30 hover:!bg-rose-500/10"
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="flex flex-col items-center py-16 text-white/45">
                            <Gem className="mb-3 h-12 w-12 opacity-40" strokeWidth={1.5} />
                            <p className="text-[14px] font-medium text-white/55">Каталог пуст</p>
                            <p className="mt-1 max-w-sm text-center text-[13px] text-white/40">
                                Добавьте первый кристалл — front и back обязательны при создании.
                            </p>
                        </div>
                    )}
                </div>
            </section>

            <Modal
                open={modalOpen}
                title={editingGem ? "Редактировать кристалл" : "Новый кристалл"}
                onCancel={() => setModalOpen(false)}
                onOk={handleSubmit}
                okText={editingGem ? "Обновить" : "Создать"}
                cancelText="Отмена"
                className="custom-modal"
                destroyOnClose
                footer={[
                    <Button
                        key="back"
                        onClick={() => setModalOpen(false)}
                        className="!rounded-xl !border-white/10 !bg-white/[0.06] !text-white/85 hover:!border-white/20 hover:!bg-white/10"
                    >
                        Отмена
                    </Button>,
                    <Button
                        key="submit"
                        type="primary"
                        onClick={handleSubmit}
                        className="!rounded-xl !border-0 !bg-emerald-500 !text-[#070A12] hover:!bg-emerald-400"
                    >
                        {editingGem ? "Обновить" : "Создать"}
                    </Button>,
                ]}
            >
                <Form form={form} layout="vertical">
                    <Form.Item
                        label="Позиция"
                        name="position"
                        rules={[{ required: true, message: "Введите номер позиции" }]}
                    >
                        <InputNumber min={1} className={inputNumGlass} controls={false} size="large" />
                    </Form.Item>

                    <Form.Item
                        label="Название"
                        name="name"
                        rules={[{ required: true, message: "Введите название" }]}
                    >
                        <Input className={inputGlass} size="large" />
                    </Form.Item>

                    <Form.Item
                        label="Front Image"
                        name="frontImage"
                        rules={
                            editingGem ? [] : [{ required: true, message: "Загрузите Front изображение" }]
                        }
                        valuePropName="fileList"
                        getValueFromEvent={normFile}
                    >
                        <Upload
                            beforeUpload={() => false}
                            maxCount={1}
                            listType="picture"
                            className="[&_.ant-upload-list-item]:!border-white/10 [&_.ant-upload-list-item]:!bg-white/[0.04]"
                            fileList={fileListFront}
                            onChange={handleFrontChange}
                            onRemove={() => {
                                setFileListFront([]);
                                form.setFieldsValue({ frontImage: [] });
                            }}
                        >
                            <Button icon={<UploadOutlined />} className={uploadBtnClass}>
                                Загрузить Front
                            </Button>
                        </Upload>
                        {editingGem && currentImages.front && (
                            <div className="mt-2 flex items-center gap-2">
                                <small className="text-[12px] text-white/45">
                                    Текущее: <span className="font-medium text-white/70">{currentImages.front}</span>
                                </small>
                                <img
                                    src={`${IMAGE_BASE_URL}${currentImages.front}`}
                                    alt=""
                                    className="h-12 w-12 rounded border border-white/10 object-contain"
                                />
                            </div>
                        )}
                    </Form.Item>

                    <Form.Item
                        label="Back Image"
                        name="backImage"
                        rules={
                            editingGem ? [] : [{ required: true, message: "Загрузите Back изображение" }]
                        }
                        valuePropName="fileList"
                        getValueFromEvent={normFile}
                    >
                        <Upload
                            beforeUpload={() => false}
                            maxCount={1}
                            listType="picture"
                            className="[&_.ant-upload-list-item]:!border-white/10 [&_.ant-upload-list-item]:!bg-white/[0.04]"
                            fileList={fileListBack}
                            onChange={handleBackChange}
                            onRemove={() => {
                                setFileListBack([]);
                                form.setFieldsValue({ backImage: [] });
                            }}
                        >
                            <Button icon={<UploadOutlined />} className={uploadBtnClass}>
                                Загрузить Back
                            </Button>
                        </Upload>
                        {editingGem && currentImages.back && (
                            <div className="mt-2 flex items-center gap-2">
                                <small className="text-[12px] text-white/45">
                                    Текущее: <span className="font-medium text-white/70">{currentImages.back}</span>
                                </small>
                                <img
                                    src={`${IMAGE_BASE_URL}${currentImages.back}`}
                                    alt=""
                                    className="h-12 w-12 rounded border border-white/10 object-contain"
                                />
                            </div>
                        )}
                    </Form.Item>

                    <Form.Item
                        label="Back Image Secondary (необязательно)"
                        name="backImageSecondary"
                        valuePropName="fileList"
                        getValueFromEvent={normFile}
                    >
                        <Upload
                            beforeUpload={() => false}
                            maxCount={1}
                            listType="picture"
                            className="[&_.ant-upload-list-item]:!border-white/10 [&_.ant-upload-list-item]:!bg-white/[0.04]"
                            fileList={fileListSecondary}
                            onChange={handleSecondaryChange}
                            onRemove={() => {
                                setFileListSecondary([]);
                                form.setFieldsValue({ backImageSecondary: [] });
                            }}
                        >
                            <Button icon={<UploadOutlined />} className={uploadBtnClass}>
                                Загрузить Back 2
                            </Button>
                        </Upload>
                        {editingGem && currentImages.secondary && (
                            <div className="mt-2 flex items-center gap-2">
                                <small className="text-[12px] text-white/45">
                                    Текущее:{" "}
                                    <span className="font-medium text-white/70">{currentImages.secondary}</span>
                                </small>
                                <img
                                    src={`${IMAGE_BASE_URL}${currentImages.secondary}`}
                                    alt=""
                                    className="h-12 w-12 rounded border border-white/10 object-contain"
                                />
                            </div>
                        )}
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
}
